import { Injector } from '@angular/core';
import { MODAL_SIZE, ModalComponentInterface, ModalConfig, ModalService } from '../../../shared/components/modal/modal.service';
import { GridColumnActionType } from '../../../shared/components/grid/grid-config';
import { PaymentMethodModel, SelfOnboardCustomerRequest } from '../../customer/customer.model';
import { ActionsService } from '../../customer/services/actions.service';
import { CardDetailsFormComponent } from '../components/card-details-form/card-details-form.component';
import { AchVerifyAccountFormComponent } from '../components/ach-verify-account-form/ach-verify-account-form.component';
import { SecureClientService } from 'app/secure-client/secure-client.service';
import { ModalComponent } from 'shared/components/modal/modal/modal.component';
import { tap } from 'rxjs/operators';
import { SelfOnboardCustomerFormComponent } from 'app/features/customer/components/self-onboard-customer/self-onboard-customer-form.component';
import { NotificationService } from 'app/shared/services/notification.service';
import { OverlayService } from 'app/shared/services/overlay.service';
import { CustomersService } from 'app/features/customer/services/customers.service';
import { Store } from '@ngrx/store';

enum PaymentMethodActionNames {
 // 'view' = 'view',
  'edit' = 'edit',
  'delete' = 'delete',
  'flashUpdate' = 'flashUpdate',
  'verifyAccount' = 'verifyAccount'
}

const openModal = (injector: Injector, data: ModalConfig): void => {
  const modalService = injector.get(ModalService);
  modalService.openModal(data);
}

const paymentMethodActionHandlers: { [key in PaymentMethodActionNames]: GridColumnActionType<PaymentMethodModel > } = {
  // [PaymentMethodActionNames.view]: {
  //   label: 'View',
  //   handler: (paymentMethod: PaymentMethodModel, component: Object, injector: Injector) => {
  //     openModal(injector, {
  //       title: 'Customer Information',
  //       body: CardDetailsFormComponent,
  //       data: {
  //         payment: paymentMethod
  //       },
  //       confirm: null,
  //       dismiss: {
  //         label: 'Close',
  //         action: (modal: ModalComponentInterface) => {
  //           modal.close();
  //         }
  //       }
  //     });
  //   }
  // },
  [PaymentMethodActionNames.edit]: {
    label: 'Edit',
    canShow: (paymentMethod: PaymentMethodModel) => paymentMethod.tokenType === 'CARD',
    handler: (paymentMethod: PaymentMethodModel, component: Object, injector: Injector) => {
      const customersService = injector.get(CustomersService);
      const overlayService = injector.get(OverlayService);
      const notificationService = injector.get(NotificationService);

      openModal(injector, {
        title: 'Edit Payment Card',
        body: CardDetailsFormComponent,
        data: {
          payment: paymentMethod,
          editMode: true,
        },
        confirm: {
          label: 'Save',
          action: (modal: ModalComponentInterface, cardDetailsFormComponent: CardDetailsFormComponent) => {
            const form = cardDetailsFormComponent.form;
            if (!form.valid) {
              form.markAllAsTouched();
              const failing: string[] = [];
              if (form.controls.expirationDate.invalid) {
                if (form.controls.expirationDate.hasError('expirationDateInvalid')) {
                  failing.push('Expiration Date (must be a future date)');
                } else if (form.controls.expirationDate.hasError('expirationDateTooFar')) {
                  failing.push('Expiration Date (too far in the future)');
                } else {
                  failing.push('Expiration Date');
                }
              }
              if (form.controls.cvv.invalid) failing.push('CVV');
              if (form.controls.cardHolderName.invalid) failing.push('Card Holder Name');
              if (form.controls.streetAddress.invalid) failing.push('Street Address');
              if (form.controls.zipCode.invalid) failing.push('Zip Code');
              cardDetailsFormComponent.invalidMessage =
                failing.length > 0
                  ? `Please fix: ${failing.join(', ')}`
                  : 'Please correct the highlighted fields before saving.';
              return;
            }

            cardDetailsFormComponent.invalidMessage = '';
            const updateData = cardDetailsFormComponent.getEditFormData();

            overlayService.showOverlay();
            customersService.updatePaymentToken(
              paymentMethod.customerId!,
              paymentMethod.id!,
              updateData
            ).subscribe({
              next: () => {
                modal.close();
                overlayService.hideOverlay();
                notificationService.success('Payment card updated successfully');
                const customerDetailsComponent = (component as any).parent;
                if (customerDetailsComponent && typeof customerDetailsComponent.loadPaymentMethods === 'function') {
                  customerDetailsComponent.loadPaymentMethods();
                }
              },
              error: (error: any) => {
                overlayService.hideOverlay();
                notificationService.error(
                  `Failed to update payment card. ${error.data?.message || error.message || ''}`
                );
              },
            });
          },
        },
        dismiss: {
          label: 'Cancel',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
  },
  [PaymentMethodActionNames.delete]: {
    label: 'Delete',
    handler: (paymentMethod: PaymentMethodModel , component: Object, injector: Injector) => {
      // Check if we're in collect payment flow or customer details
      const parentContext = (component as any).parent?.parent;
      const hasAllowNext = parentContext && '$allowNext' in parentContext;
      
      if (hasAllowNext) {
        parentContext.$allowNext = false;
      }
      
      const actionsService = injector.get(ActionsService);
      openModal(injector!, {
        title: 'Delete Payment Method',
        body: `Are you sure you want to delete payment #${ paymentMethod.account }?`,
        dismiss: {
          label: 'Cancel',
          action(modal: ModalComponentInterface) {
            modal.close();
            if (hasAllowNext) {
              parentContext.$allowNext = true;
            }
          }
        },
        confirm: {
          label: 'Delete',
          action: (modal: ModalComponentInterface) => {
            actionsService.handlePaymentDeleteAction(
              paymentMethod,
              () => { 
                modal.close(); 
                if (hasAllowNext) {
                  parentContext.$allowNext = true;
                }
                
                      // If we're in customer details, reload payment methods
                      const customerDetailsComponent = (component as any).parent;
                      if (customerDetailsComponent && typeof customerDetailsComponent.loadPaymentMethods === 'function') {
                        customerDetailsComponent.loadPaymentMethods();
                      }
                      // Also reload ACH payment methods if they're available
                      if (customerDetailsComponent && typeof customerDetailsComponent.loadAchPaymentMethods === 'function') {
                        customerDetailsComponent.loadAchPaymentMethods();
                      }
              }
            );
          }
        }
      });
    },
  },
  [PaymentMethodActionNames.verifyAccount]: {
    label: 'Verify Account',
    handler: (paymentMethod: PaymentMethodModel, component: Object, injector: Injector) => {
      const secureClient = injector.get(SecureClientService);
      const overlayService = injector.get(OverlayService);
      const notificationService = injector.get(NotificationService);

      openModal(injector, {
        title: 'Verify ACH Account',
        size: MODAL_SIZE.sm,
        body: AchVerifyAccountFormComponent,
        data: {
          payment: paymentMethod,
        },
        confirm: {
          label: 'Verify',
          action: (modal: ModalComponentInterface, form: AchVerifyAccountFormComponent) => {
            if (!form.form.valid) {
              form.form.markAllAsTouched();
              return;
            }
            if (form.isVerifying) {
              return;
            }

            form.isVerifying = true;
            overlayService.showOverlay();
            secureClient
              .verifyAchAccount(paymentMethod.customerId!, paymentMethod.id!, form.accountNumber)
              .subscribe({
                next: (result) => {
                  form.isVerifying = false;
                  overlayService.hideOverlay();

                  if (result.verified) {
                    // Nothing left to do in the dialog once the answer is yes,
                    // so close it and let the notification carry the outcome.
                    modal.close();
                    notificationService.success(
                      result.basis === 'LAST4'
                        ? 'Last 4 digits match. This account predates full verification.'
                        : 'Account number verified',
                    );
                    return;
                  }

                  // On a mismatch the dialog stays open so the user can correct
                  // and retry, with the reason shown inline next to the field.
                  form.setResult(result);
                  notificationService.error('Account number does not match the account on file');
                },
                error: (error: any) => {
                  form.isVerifying = false;
                  overlayService.hideOverlay();
                  const message = error.data?.message || error.message || 'Please try again.';
                  form.setError(message);
                  notificationService.error(`Could not verify account. ${message}`);
                },
              });
          },
        },
        dismiss: {
          label: 'Close',
          action: (modal: ModalComponentInterface) => {
            modal.close();
          },
        },
      });
    },
  },
  [PaymentMethodActionNames.flashUpdate]: {
    label: 'Request Update',
    handler: (payment: PaymentMethodModel, component: Object, injector: Injector) => {
      const customerService = injector.get(CustomersService);
      const overlayService = injector.get(OverlayService);
      const notificationService = injector.get(NotificationService);
      
      // Get the collect component instance to access its form
      const collectComponent = (component as any).parent?.parent;
      const customer = collectComponent?.form?.get('customer')?.value;

      if (!customer) {
        notificationService.error('Please select a customer first');
        return;
      }

      // The account being updated says which kind of form to send — not the wizard's mode, which
      // does not exist on the customer screen (where this used to read 'card' for ACH rows too).
      const paymentType = payment.tokenType === 'ACH' || payment.accountType === 'ACH' ? 'ach' : 'card';

      const initialData: SelfOnboardCustomerRequest = {
        customerAccountNumber: customer.companyAccountNumber,
        customerEmail: customer.contactEmail,
        type: 'paymentflashupdate',
        paymentRequestId: payment.id,
        customerId: customer.id,
        paymentType: paymentType

      };
      console.log('initialData', initialData);

      openModal(injector!, {
        title: 'Invite a customer to update their payment method',
        body: SelfOnboardCustomerFormComponent,
        data: initialData,
        confirm: {
          label: 'Send Request',
          action: (modal, selfOnboardCustomerFormComponent: SelfOnboardCustomerFormComponent) => {
            const form = selfOnboardCustomerFormComponent.form;
            if (!form.valid) {
              form.markAllAsTouched();
              return;
            }
            const formData = form.getRawValue();

            const selfOnboardCustomerRequest: SelfOnboardCustomerRequest = {
              customerEmail: formData.customerEmail || '',
              customerAccountNumber: formData.customerAccountNumber || '',
              type: 'paymentflashupdate',
              paymentRequestId: payment.id,
              customerId: customer.id,
              paymentType: paymentType

            };

            console.log('selfOnboardCustomerRequest', selfOnboardCustomerRequest);

            customerService.createSelfOnboardCustomerLink(selfOnboardCustomerRequest)
              .pipe(
                tap(() => overlayService.showOverlay())
              )
              .subscribe({
                next: () => {
                  modal.close();
                  overlayService.hideOverlay();
                  notificationService.success('Customer payment method flash update request sent successfully');
                },
                error: (error) => {
                  overlayService.hideOverlay();
                  notificationService.error(error.message);
                }
              });
          },
        },
        dismiss: (modal: ModalComponentInterface) => {
          modal.close();
        }
      });
    }
  }
}

export const paymentMethodActions = [
 // paymentMethodActionHandlers[PaymentMethodActionNames.view],
 paymentMethodActionHandlers[PaymentMethodActionNames.edit],
 paymentMethodActionHandlers[PaymentMethodActionNames.flashUpdate],
 paymentMethodActionHandlers[PaymentMethodActionNames.delete],
];

/**
 * Actions for the ACH on File grid. Kept separate from `paymentMethodActions`
 * rather than gated on `tokenType`, because ACH rows are served by a different
 * endpoint that does not always populate it.
 */
export const achPaymentMethodActions = [
 paymentMethodActionHandlers[PaymentMethodActionNames.verifyAccount],
 paymentMethodActionHandlers[PaymentMethodActionNames.flashUpdate],
 paymentMethodActionHandlers[PaymentMethodActionNames.delete],
];